import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Download, Inbox, Plus, Search } from '../../../icons';
import {
  listEmployees, downloadEmployeesCsv, EMPLOYMENT_STATUS_LABEL, type EmployeeListDto, type EmployeeRowDto,
} from '../../../api/hr-employee';
import { employmentTypeOptions } from '../../../lib/employment-type-i18n';
import { getSharedLang, subscribeSharedLang } from '../../../lib/base-i18n';
import { listDivisions, listTeams, type OrgDivision, type OrgTeam } from '../../../api/admin-org';
import { useCompanyCodes } from '../../../lib/company-codes';
import type { ContractExpiryItem } from './employee-hr-types';
// 카드 넷(EmployeeKpis) → 표 위 «할 일» 알림 줄(2026-10-18 · web 레인 W10-25 · 렌즈 Toss · 사용자 결정 B안).
//   EmployeeKpis.tsx 는 지우지 않는다 — 현황 숫자는 인사 통계(26)로 옮긴다(W10-26).
import { EmployeeNotices } from './EmployeeNotices';
import { EmployeeOrgTree, inSelection, selectionPath, type OrgSelection } from './EmployeeOrgTree';
import {
  ERRORS, EMPLOYEE_NEW_PATH, STATUS_TONE, UPCOMING_HIRE_LABEL, employeePath, errorCode, initialOf, isUpcomingHire, utcDatePlus,
  UPCOMING_RESIGN_LABEL, resignBadge,
} from './employees-shared';
import { saveBlob } from '../../../lib/save-file';
// 손짠 머리 → 정본 PageHeader · 경로(링크) · 범위 칩(2026-10-17 · web 레인 W7b — lib/page-crumbs.ts)
import { PageHeader } from '../../../components/shared';
// 표 쪽 넘김(2026-10-18 · web 레인 W10-25 · 재평 25 고칠 1)
import { Pagination } from '../../../components/shared';
import { crumbsFor, scopeFor } from '../../../lib/page-crumbs';
import './employees-hr.css';

/** 직원 목록 한 쪽 줄 수(2026-10-18 · web 레인 W10-25) — 시안 55 «1–10 / 총 24» 와 같은 10줄. */
const LIST_PAGE_SIZE = 10;

/**
 * 직원 명부 — 목록 (2026-09-13 · H · `/erp/hr/employees`).
 *
 * 시안 axi-employees-hr-list: 상단 바 → KPI 4 → [조직도 | 직원 목록]. **행을 누르면 상세 화면으로 이동**한다
 * (목록 옆 상세 패널은 없다 — 사용자 결정). 종전 한 화면짜리 명부는 `/erp/hr/employees/legacy` 로 남는다.
 */
type Filter = 'all' | 'active' | 'upcoming' | 'inactive' | 'noHire' | 'resign';

export function EmployeesListPage(): JSX.Element {
  const navigate = useNavigate();
  const codes = useCompanyCodes('hr');
  // 고용형태 기본 이름은 언어를 따른다(수습 · 인턴 포함 · 서버 #1222) — 회사 코드가 그 위에 덮는다.
  const lang = useSyncExternalStore(subscribeSharedLang, getSharedLang, getSharedLang);
  const typeLabel = useMemo(() => {
    const list = codes.apply('employment_type', employmentTypeOptions(lang));
    return Object.fromEntries(list.map((t) => [t.key, t.label])) as Record<string, string>;
  }, [codes, lang]);

  const [rows, setRows] = useState<EmployeeRowDto[]>([]);
  const [missing, setMissing] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [divisions, setDivisions] = useState<OrgDivision[] | null>(null);
  const [teams, setTeams] = useState<OrgTeam[] | null>(null);
  const [selection, setSelection] = useState<OrgSelection>({ kind: 'all' });
  const [filter, setFilter] = useState<Filter>('all');
  // 서버 요약 칸(계약 §18.4 · §18.5) — 옛 서버는 안 준다(없으면 KPI 가 종전처럼 「데이터 준비 중」).
  const [summary, setSummary] = useState<Pick<EmployeeListDto, 'upcomingHires' | 'probation' | 'probationMonths' | 'probationMonthsSource' | 'inputCompletion' | 'upcomingResigns'>>({});
  // 목록 내려받기 — 서버에 물은 검색어(입력 중인 글자가 아니라 Enter 로 보낸 것)를 파일에도 싣는다.
  const [appliedSearch, setAppliedSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (q?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await listEmployees(q ? { search: q } : undefined);
      setRows(data.rows);
      setMissing(data.missingHireDate);
      setSummary({
        upcomingHires: data.upcomingHires, probation: data.probation,
        probationMonths: data.probationMonths, probationMonthsSource: data.probationMonthsSource,
        inputCompletion: data.inputCompletion,
        upcomingResigns: data.upcomingResigns,
      });
    } catch (err) {
      setError(ERRORS[errorCode(err)] ?? '직원 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    // 조직도는 곁가지다 — 못 읽어도 목록은 전체로 선다.
    Promise.all([listDivisions(), listTeams()])
      .then(([d, t]) => { setDivisions(d); setTeams(t); })
      .catch(() => { setDivisions(null); setTeams(null); });
  }, []);
  // ★ 조직 인원수(org-leaders) · 재직 인원 추이(headcount-trend)는 카드 넷만 쓰던 조회라 W10-25 에서 이 화면이 더는 부르지 않는다.
  //   두 API 와 `EmployeeKpis` 의 셈은 그대로 남아 인사 통계(26)에서 다시 쓴다.

  const inOrg = rows.filter((r) => inSelection(r.teamId, selection, teams));
  // ★ 입사 예정(upcomingHire)은 재직이 아니다(계약 §18.0 · §18.4) — 「재직」 은 서버 `?status=ACTIVE` 와 같은 수,
  //   「입사 예정」 은 `?status=UPCOMING` 과 같은 사람. 조직도 · 보기 거르기가 모두 이 화면 안에서 걸리므로 같은 행에서 가른다.
  const counts = {
    all: inOrg.length,
    active: inOrg.filter((r) => r.employmentStatus === 'ACTIVE' && !r.upcomingHire).length,
    upcoming: inOrg.filter(isUpcomingHire).length,
    inactive: inOrg.filter((r) => r.employmentStatus !== 'ACTIVE').length,
    noHire: inOrg.filter((r) => !r.hireDate).length,
    // 퇴사 예정(§18.4) — 재직에 남는 사람이라 「재직」 수에서 빼지 않는다.
    resign: inOrg.filter((r) => !!r.upcomingResign).length,
  };
  const shown = inOrg.filter((r) => (
    filter === 'all' ? true
      : filter === 'active' ? r.employmentStatus === 'ACTIVE' && !r.upcomingHire
        : filter === 'upcoming' ? isUpcomingHire(r)
          : filter === 'inactive' ? r.employmentStatus !== 'ACTIVE'
            : filter === 'resign' ? !!r.upcomingResign
              : !r.hireDate
  ));
  // 쪽 넘김(2026-10-18 · web 레인 W10-25 · 재평 25 고칠 1 «전체 24 인데 7줄 · 쪽 넘김도 나머지도 없다») — 10줄씩.
  //   거르기 · 조직 · 목록이 바뀌면 첫 쪽으로 간다.
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [filter, selection, rows]);
  const pageCount = Math.max(1, Math.ceil(shown.length / LIST_PAGE_SIZE));
  const curPage = Math.min(page, pageCount);
  const paged = shown.slice((curPage - 1) * LIST_PAGE_SIZE, curPage * LIST_PAGE_SIZE);

  // 30일 안에 끝나는 수습 — 서버 KPI 규칙(§18.5)과 같게: 수습 재직자(입사 예정 제외) · 오늘 ≤ 종료일 ≤ 오늘 + 30일.
  const expiries: ContractExpiryItem[] | null = summary.probation ? (() => {
    const from = utcDatePlus(0);
    const to = utcDatePlus(30);
    return rows
      .filter((r) => r.employmentType === 'PROBATION' && r.employmentStatus === 'ACTIVE' && !r.upcomingHire
        && !!r.probationEndDate && r.probationEndDate >= from && r.probationEndDate <= to)
      .map((r) => ({ userId: r.userId, name: r.name, kind: 'PROBATION' as const, endsOn: r.probationEndDate ?? '' }))
      .sort((a, b) => a.endsOn.localeCompare(b.endsOn));
  })() : null;

  // 목록 내려받기(계약 §18.7) — 화면의 거르기 중 서버가 받는 것(검색 · 팀 · 재직/입사 예정)만 싣고, 못 싣는 것은 단추가 말한다.
  const exportParams = {
    ...(appliedSearch ? { search: appliedSearch } : {}),
    ...(selection.kind === 'team' ? { teamId: selection.id } : {}),
    ...(filter === 'active' ? { status: 'ACTIVE' } : filter === 'upcoming' ? { status: 'UPCOMING' } : {}),
  };
  const notInFile = [
    selection.kind === 'division' || selection.kind === 'direct' ? '본부 선택' : null,
    filter === 'inactive' ? '휴직 · 퇴사 보기' : filter === 'noHire' ? '입사일 없음 보기' : filter === 'resign' ? `${UPCOMING_RESIGN_LABEL} 보기` : null,
  ].filter(Boolean);
  const exportTitle = `목록 내려받기 (CSV · 이메일 · 전화 · 생일은 담지 않습니다)${notInFile.length ? ` — ${notInFile.join(' · ')}는 파일에 반영되지 않습니다` : ''}`;
  const onExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const { blob, filename } = await downloadEmployeesCsv(exportParams);
      saveBlob(blob, filename);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 403 ? (ERRORS.FORBIDDEN ?? '권한이 없습니다.') : '목록을 내려받지 못했습니다.');
    } finally {
      setExporting(false);
    }
  };

  const chip = (value: Filter, label: string) => (
    <label className="hre-choice">
      <input type="radio" name="hre-filter" checked={filter === value} onChange={() => setFilter(value)} />
      <span>{label} {counts[value]}</span>
    </label>
  );

  return (
    <div className="hre">
      {/* ★ 제목은 메뉴 이름과 같다 — 인사 트리 「직원 관리」(2026-09-13 · K 사용자 결정, 시안 제목 「직원 명부」 와 다름 · lib/hr-menu-titles.test). */}
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/employees')}
        scope={scopeFor('/erp/hr/employees')}
        title="직원 관리"
        actions={(
          <>
            <label className="hre-search">
              <span className="hre-sr">이름 · 사번 검색</span>
              <Search aria-hidden />
              <input
                type="search" placeholder="이름, 사번으로 검색 (Enter)" value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { const q = search.trim(); setAppliedSearch(q); void load(q); } }}
              />
            </label>
            <Link className="hre-btn" to={EMPLOYEE_NEW_PATH}><Plus aria-hidden />입사 처리</Link>
          </>
        )}
      />

      {error && <p className="hre-alert" role="alert"><AlertCircle aria-hidden />{error}</p>}

      <EmployeeNotices
        rows={rows} missingHireDate={missing} inputCompletion={summary.inputCompletion ?? null}
        probation={summary.probation ?? null}
        probationMonths={summary.probationMonths ?? null} probationMonthsSource={summary.probationMonthsSource ?? null}
        expiries={expiries} onShowNoHire={() => setFilter('noHire')}
      />

      <div className="hre-work">
        <EmployeeOrgTree
          divisions={divisions} teams={teams} total={rows.length}
          selection={selection} onSelect={(s) => { setSelection(s); }}
        />

        <section className="hre-card hre-listcard" aria-label="직원 목록">
          <div className="hre-cb">
            <div className="hre-ch">
              <h2>직원 목록</h2>
              <div className="hre-end">
                <span className="hre-chips" role="radiogroup" aria-label="보기">
                  {chip('all', '전체')}
                  {chip('active', '재직')}
                  {chip('upcoming', UPCOMING_HIRE_LABEL)}
                  {chip('resign', UPCOMING_RESIGN_LABEL)}
                  {chip('inactive', '휴직 · 퇴사')}
                  {chip('noHire', '입사일 없음')}
                </span>
                <button type="button" className="hre-ghost" aria-label="목록 내려받기" title={exportTitle} disabled={exporting}
                  onClick={() => { void onExport(); }}>
                  <Download aria-hidden />
                </button>
              </div>
            </div>
            <span className="hre-path">{selectionPath(selection)} · 행을 누르면 직원 상세로 갑니다</span>

            {loading ? (
              <p className="hre-path">불러오는 중…</p>
            ) : shown.length === 0 ? (
              <div className="hre-empty">
                <Inbox aria-hidden />
                <b>{rows.length === 0 ? '직원이 없습니다' : '이 조건에 맞는 직원이 없습니다'}</b>
                <span>{rows.length === 0 ? '「입사 처리」로 사람을 들입니다' : '조직도에서 다른 조직을 고르거나 보기를 「전체」로 바꾸세요'}</span>
              </div>
            ) : (
              <div className="hre-tw">
                <table className="hre-table">
                  <thead><tr><th>직원</th><th className="c-num">사번</th><th>고용형태</th><th>입사일</th><th className="c-tenure">근속</th><th className="r">올해 연차</th><th className="r">상태</th></tr></thead>
                  <tbody>
                    {paged.map((r) => (
                      <tr key={r.userId} className="hre-row" onClick={() => navigate(employeePath(r.userId))}>
                        <td>
                          <span className="hre-pickrow">
                            <span className="hre-avatar" aria-hidden>{initialOf(r.name)}</span>
                            <span>
                              <Link to={employeePath(r.userId)} onClick={(e) => e.stopPropagation()}><b>{r.name}</b></Link>
                              <small>
                                {r.teamName ?? '팀 미배정'} · {r.position ?? '직위 없음'}
                                {/* 좁은 카드에서만 보인다 — 사번 열을 숨긴 대신(CSS @container hre-list) */}
                                <span className="hre-subnum num"> · {r.employeeNumber ?? '사번 없음'}</span>
                              </small>
                            </span>
                          </span>
                        </td>
                        <td className="num c-num">{r.employeeNumber ?? '—'}</td>
                        <td>{r.employmentType ? (typeLabel[r.employmentType] ?? r.employmentType) : '미설정'}</td>
                        <td className="num">{r.hireDate ?? <span className="hre-badge hre-badge--bad">입사일 없음</span>}</td>
                        <td className="num c-tenure">{r.tenureLabel ?? '—'}</td>
                        <td className="r num">{r.annualDays === null ? '미설정' : `${r.annualDays}일`}</td>
                        <td className="r">
                          {isUpcomingHire(r) ? (
                            <span className="hre-badge hre-badge--pur">{UPCOMING_HIRE_LABEL}</span>
                          ) : (
                            <span className={`hre-badge hre-badge--${STATUS_TONE[r.employmentStatus] ?? 'slate'}`}>
                              {EMPLOYMENT_STATUS_LABEL[r.employmentStatus] ?? r.employmentStatus}
                            </span>
                          )}
                          {/* 퇴사 예정 · 처리 대기(§18.4) — 재직 배지 옆에 더한다(재직 판정은 그대로) */}
                          {(() => { const rb = resignBadge(r); return rb && <> <span className={`hre-badge hre-badge--${rb.tone}`} title={rb.title}>{rb.label}</span></>; })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="hre-listfoot">
              <span className="num">{rows.length}명 중 {shown.length}명 표시</span>
              {pageCount > 1 && (
                <>
                  <span className="num">{(curPage - 1) * LIST_PAGE_SIZE + 1}–{Math.min(shown.length, curPage * LIST_PAGE_SIZE)} / 총 {shown.length}</span>
                  <Pagination
                    mode="offset" ariaLabel="직원 목록 쪽" page={curPage} pageSize={LIST_PAGE_SIZE}
                    total={shown.length} onPageChange={setPage} showSummary={false}
                  />
                </>
              )}
              {filter !== 'noHire' && missing > 0 && (
                <button type="button" className="hre-btn hre-btn--line hre-btn--sm" onClick={() => setFilter('noHire')}>입사일 없는 직원만 보기</button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default EmployeesListPage;
